import { apiFetch } from '@/lib/api-client';

/**
 * Probecover Stage 1 — API client for /api/figuren/orders.
 *
 * Same house pattern as `pwa/src/features/book/api.ts` — plain
 * path-to-Promise wrappers around the shared `apiFetch`. The two
 * endpoints are backed by `src/book/probe/probe.controller.ts`.
 */

export type ProbePersonType = 'person' | 'tier';
export type ProbeGeschlecht = 'maennlich' | 'weiblich' | 'keineAngabe';
export type ProbeStatus = 'probe_rendering' | 'probe_ready' | 'probe_render_failed';
export type ProbeVenueVariant = 'A' | 'B' | 'C';

export interface CreateProbePersonPayload {
  readonly personType: ProbePersonType;
  readonly ageYears: number;
  readonly geschlecht: ProbeGeschlecht;
  readonly species?: string;
  readonly photoKey: string;
}

export interface CreateProbePayload {
  readonly persons: readonly CreateProbePersonPayload[];
}

export interface CreateProbeResponse {
  readonly probeId: string;
}

export interface ProbeStatusResponse {
  readonly probeId: string;
  readonly status: ProbeStatus;
  readonly watermarkedUrl: string | null;
  readonly createdAt: string;
  readonly venueVariant: ProbeVenueVariant;
}

export function createProbe(payload: CreateProbePayload): Promise<CreateProbeResponse> {
  // `apiFetch` already JSON.stringifies the body (api-client.ts:111)
  // and sets Content-Type: application/json (api-client.ts:99-101).
  // Passing `JSON.stringify(payload)` here double-encodes: the wire
  // payload becomes a JSON string whose value is another JSON string,
  // and Nest's body-parser surfaces that as the client-visible error
  // `Unexpected token '"', "\"{\\\"persons\\\":..." is not valid JSON`.
  // Pass the plain object; `apiFetch` does the one encode.
  return apiFetch<CreateProbeResponse>('/api/figuren/orders', {
    method: 'POST',
    body: payload,
  });
}

export function fetchProbeStatus(probeId: string): Promise<ProbeStatusResponse> {
  return apiFetch<ProbeStatusResponse>(`/api/figuren/orders/${probeId}`);
}

/** Stage 2 P4 — per-figure transfer payload for the Buch-machen flow. */
export interface ProbeTransferFigureDto {
  readonly characterKey: string;
  readonly personType: ProbePersonType;
  readonly ageYears: number;
  readonly geschlecht: ProbeGeschlecht;
  readonly species: string;
  readonly photoDownloadUrl: string;
  readonly photoContentType: 'image/jpeg' | 'image/png' | 'image/webp';
  readonly photoFilename: string;
}

export interface ProbeTransferResponse {
  readonly probeId: string;
  readonly figures: readonly ProbeTransferFigureDto[];
}

export function fetchProbeTransfer(probeId: string): Promise<ProbeTransferResponse> {
  return apiFetch<ProbeTransferResponse>(`/api/figuren/orders/${probeId}/transfer`);
}
